import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Component, type ReactNode} from 'react'

interface ErrorBoundaryProps {
  fallback: ReactNode
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = {
      error: null,
    }
  }

  /**
   * Invoked when an error is thrown in the child component,
   * and used to update state in a concurrent friendly manner
   */
  static getDerivedStateFromError(error: Error) {
    return {error}
  }

  /**
   * When `react_centralized_error_handling` feature flag is enabled:
   * - Error reporting is handled centrally via React 19's `onCaughtError` callback
   *
   * When feature flag is disabled (legacy behavior):
   * - Re-throws the error out of the rendering context to propagate to Failbot
   */
  override componentDidCatch(error: Error) {
    if (!isFeatureEnabled('react_centralized_error_handling')) {
      // Legacy behavior: re-throw the error out of the rendering context
      // to ensure it propagates to failbot
      setTimeout(() => {
        throw error
      })
    }
    // When flag is enabled, React 19's onCaughtError handles reporting
  }

  override render() {
    if (!this.state.error) return this.props.children

    return this.props.fallback
  }
}
